document.addEventListener("DOMContentLoaded", function () {
  "use strict";

  /* =========================================================
     KEYS
  ========================================================= */

  const GIFT_CARD_KEY = "selectedGiftCardData";
  const PRODUCTS_KEY = "selectedGiftProducts";
  const CHECKOUT_ADDRESS_KEY = "giftCheckoutAddress";
  const LAST_GIFT_VARIANT_IDS_KEY = "lastGiftCartVariantIds";

  const shopifyRoot =
    (window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root) ||
    "/";

  /* =========================================================
     COUNTRY CODES
  ========================================================= */

  const COUNTRY_CODES = {
    "United States": "US",
    "United States of America": "US",
    USA: "US",
    Canada: "CA",
    "United Kingdom": "GB",
    "Great Britain": "GB",
    Australia: "AU",
    India: "IN",
    "New Zealand": "NZ",
    Ireland: "IE",
    France: "FR",
    Germany: "DE",
    Italy: "IT",
    Spain: "ES",
    Netherlands: "NL",
    Belgium: "BE",
    Switzerland: "CH",
    Austria: "AT",
    Sweden: "SE",
    Norway: "NO",
    Denmark: "DK",
    Finland: "FI",
    Poland: "PL",
    Portugal: "PT",
    Mexico: "MX",
    Brazil: "BR",
    Japan: "JP",
    "South Korea": "KR",
    "Korea, Republic of": "KR",
    Singapore: "SG",
    "Hong Kong": "HK",
    "United Arab Emirates": "AE",
    "Saudi Arabia": "SA",
    "South Africa": "ZA",
    Philippines: "PH",
    Malaysia: "MY",
    Indonesia: "ID",
    Thailand: "TH",
    Vietnam: "VN",
    China: "CN",
    Taiwan: "TW",
    Israel: "IL",
    Turkey: "TR",
    Greece: "GR",
    "Czech Republic": "CZ",
    Czechia: "CZ",
    Hungary: "HU",
    Romania: "RO",
    "Slovak Republic": "SK",
    Slovakia: "SK",
    Croatia: "HR",
    Luxembourg: "LU",
    "Puerto Rico": "PR"
  };

  /* =========================================================
     STORAGE HELPERS
  ========================================================= */

  function getSelectedGiftCard() {
    try {
      const raw = localStorage.getItem(GIFT_CARD_KEY);

      if (!raw) {
        return null;
      }

      const giftCard = JSON.parse(raw);

      if (!giftCard || !giftCard.variantId) {
        return null;
      }

      return giftCard;
    } catch (error) {
      console.error("🎁 Invalid gift card data:", error);
      return null;
    }
  }

  function getSelectedProducts() {
    try {
      const raw = localStorage.getItem(PRODUCTS_KEY);

      if (!raw) {
        return [];
      }

      const products = JSON.parse(raw);

      if (!Array.isArray(products)) {
        return [];
      }

      return products.filter(function (product) {
        return product && product.variantId;
      });
    } catch (error) {
      console.error("🎁 Invalid selected products data:", error);
      return [];
    }
  }

  function getLastGiftVariantIds() {
    try {
      const raw = localStorage.getItem(LAST_GIFT_VARIANT_IDS_KEY);

      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map(function (id) {
          return Number(id);
        })
        .filter(function (id) {
          return Boolean(id);
        });
    } catch (error) {
      return [];
    }
  }

  function saveLastGiftVariantIds(ids) {
    try {
      const uniqueIds = [];
      const seen = {};

      (ids || []).forEach(function (id) {
        const variantId = Number(id);

        if (!variantId || seen[variantId]) {
          return;
        }

        seen[variantId] = true;
        uniqueIds.push(variantId);
      });

      localStorage.setItem(
        LAST_GIFT_VARIANT_IDS_KEY,
        JSON.stringify(uniqueIds)
      );
    } catch (error) {
      console.warn("🎁 Unable to store last gift cart variants:", error);
    }
  }

  /* =========================================================
     CHECKOUT ADDRESS STORAGE
  ========================================================= */

  function getStoredGiftCheckoutAddress() {
    try {
      const sessionData =
        sessionStorage.getItem(CHECKOUT_ADDRESS_KEY);

      const localData =
        localStorage.getItem(CHECKOUT_ADDRESS_KEY);

      const raw = sessionData || localData;

      if (!raw) {
        return null;
      }

      const address = JSON.parse(raw);

      if (!address || !address.address1) {
        return null;
      }

      return address;
    } catch (error) {
      console.warn(
        "🎁 Could not read stored checkout address:",
        error
      );

      return null;
    }
  }

  function storeGiftCheckoutAddress(address) {
    if (!address || !address.address1) {
      return;
    }

    const payload = JSON.stringify(address);

    try {
      sessionStorage.setItem(
        CHECKOUT_ADDRESS_KEY,
        payload
      );
    } catch (error) {
      console.warn(
        "🎁 Session storage unavailable:",
        error
      );
    }

    try {
      localStorage.setItem(
        CHECKOUT_ADDRESS_KEY,
        payload
      );
    } catch (error) {
      console.warn(
        "🎁 Local storage unavailable:",
        error
      );
    }
  }

  /* =========================================================
     NAME HELPERS
  ========================================================= */

  function splitRecipientName(fullName) {
    const parts = String(fullName || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (parts.length === 0) {
      return {
        firstName: "",
        lastName: ""
      };
    }

    if (parts.length === 1) {
      return {
        firstName: parts[0],
        lastName: ""
      };
    }

    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(" ")
    };
  }

  /* =========================================================
     COUNTRY HELPERS
  ========================================================= */

  function resolveCountryCode(
    countryValue,
    countryLabel
  ) {
    const raw = String(countryValue || "").trim();
    const label = String(countryLabel || "").trim();

    if (/^[A-Za-z]{2}$/.test(raw)) {
      return raw.toUpperCase();
    }

    return (
      COUNTRY_CODES[raw] ||
      COUNTRY_CODES[label] ||
      raw ||
      label
    );
  }

  function resolveProvinceCode(
    provinceValue,
    provinces
  ) {
    const input = String(provinceValue || "").trim();

    if (!input) {
      return "";
    }

    const normalized = input.toLowerCase();

    const list = Array.isArray(provinces)
      ? provinces
      : [];

    for (let i = 0; i < list.length; i += 1) {
      const name = String(
        (list[i] && list[i][0]) || ""
      ).trim();

      const code = String(
        (list[i] && list[i][1]) || ""
      ).trim();

      if (
        normalized === name.toLowerCase() ||
        normalized === code.toLowerCase()
      ) {
        return code || name;
      }
    }

    return input;
  }

  /* =========================================================
     COUNTRY OPTION DATA
  ========================================================= */

  function getCountryOptionData(form) {
    const select =
      form &&
      form.elements
        ? form.elements.gift_recipient_country
        : null;

    if (
      !select ||
      !select.options ||
      select.selectedIndex < 0
    ) {
      return {
        value: "",
        label: "",
        provinces: []
      };
    }

    const option =
      select.options[select.selectedIndex];

    let provinces = [];

    try {
      provinces = JSON.parse(
        option.getAttribute("data-provinces") || "[]"
      );
    } catch (error) {
      provinces = [];
    }

    return {
      value: String(option.value || "").trim(),
      label: String(
        option.textContent || ""
      ).trim(),
      provinces: provinces
    };
  }

  /* =========================================================
     BUILD CHECKOUT ADDRESS
  ========================================================= */

  function buildCheckoutAddressFromForm(
    form,
    values
  ) {
    const names = splitRecipientName(
      values.recipientName
    );

    const countryOption =
      getCountryOptionData(form);

    return {
      firstName: names.firstName,
      lastName: names.lastName,
      address1: values.recipientAddress || "",
      address2: values.recipientApartment || "",
      city: values.recipientCity || "",
      province: resolveProvinceCode(
        values.recipientState,
        countryOption.provinces
      ),
      zip: values.recipientPostal || "",
      country: resolveCountryCode(
        countryOption.value,
        countryOption.label
      ),
      phone: values.recipientPhone || ""
    };
  }

  /* =========================================================
     BUILD CHECKOUT ADDRESS FROM CART
  ========================================================= */

  function buildCheckoutAddressFromAttributes(
    attrs
  ) {
    if (!attrs) {
      return null;
    }

    if (attrs._gift_shipping_address) {
      try {
        const parsed = JSON.parse(
          attrs._gift_shipping_address
        );

        if (
          parsed &&
          parsed.address1
        ) {
          return parsed;
        }
      } catch (error) {}
    }

    if (
      attrs["Gift Order"] !== "Yes" ||
      !attrs["Recipient Address"]
    ) {
      return null;
    }

    const names = splitRecipientName(
      attrs["Recipient Name"]
    );

    return {
      firstName: names.firstName,
      lastName: names.lastName,
      address1:
        attrs["Recipient Address"] || "",
      address2:
        attrs["Recipient Apartment"] || "",
      city:
        attrs["Recipient City"] || "",
      province:
        attrs._gift_province ||
        attrs["Recipient State"] ||
        "",
      zip:
        attrs["Recipient Postal Code"] || "",
      country: resolveCountryCode(
        attrs._gift_country ||
          attrs["Recipient Country"] ||
          "",
        attrs["Recipient Country"] || ""
      ),
      phone:
        attrs["Recipient Phone"] || ""
    };
  }

  /* =========================================================
     CART GIFT CHECK
  ========================================================= */

  function cartLooksLikeGift(cart) {
    if (!cart) {
      return false;
    }

    const attributes =
      cart.attributes || {};

    if (
      attributes["Gift Order"] === "Yes" ||
      attributes._gift_shipping_address
    ) {
      return true;
    }

    const items =
      Array.isArray(cart.items)
        ? cart.items
        : [];

    return items.some(function (item) {
      const properties =
        item && item.properties
          ? item.properties
          : {};

      return (
        properties["Gift Order"] === "Yes" ||
        properties["Gift Item Type"] === "Gift Card" ||
        properties["Gift Item Type"] === "Gift Product"
      );
    });
  }

  /* =========================================================
     CHECKOUT URL
  ========================================================= */

  function buildCheckoutUrl(baseHref) {
    const address =
      getStoredGiftCheckoutAddress();

    if (
      !address ||
      !address.address1
    ) {
      return baseHref;
    }

    let url;

    try {
      url = new URL(
        baseHref,
        window.location.origin
      );
    } catch (error) {
      url = new URL(
        shopifyRoot + "checkout",
        window.location.origin
      );
    }

    const fields = {
      "checkout[shipping_address][first_name]":
        address.firstName,

      "checkout[shipping_address][last_name]":
        address.lastName,

      "checkout[shipping_address][address1]":
        address.address1,

      "checkout[shipping_address][address2]":
        address.address2,

      "checkout[shipping_address][city]":
        address.city,

      "checkout[shipping_address][province]":
        address.province,

      "checkout[shipping_address][country]":
        address.country,

      "checkout[shipping_address][zip]":
        address.zip,

      "checkout[shipping_address][phone]":
        address.phone
    };

    Object.keys(fields).forEach(function (key) {
      const value = fields[key];

      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        url.searchParams.set(
          key,
          String(value)
        );
      }
    });

    return (
      url.pathname +
      url.search +
      url.hash
    );
  }

  /* =========================================================
     CHECKOUT BUTTON HELPERS
  ========================================================= */

  function isAcceleratedCheckoutTarget(
    target
  ) {
    if (
      !target ||
      !target.closest
    ) {
      return false;
    }

    return Boolean(
      target.closest(
        ".shopify-payment-button, " +
        ".additional-checkout-buttons, " +
        ".dynamic-checkout, " +
        "[data-shopify-buttoncontainer], " +
        ".shopify-cleanslate"
      )
    );
  }

  function isRegularCheckoutHref(
    href
  ) {
    if (!href) {
      return false;
    }

    if (
      /shop\.app|paypal\.com|google\.com|apple\.com/i.test(
        href
      )
    ) {
      return false;
    }

    return (
      /\/checkout(\/|\?|$|#)/.test(
        href
      ) ||
      /\/cart\/checkout/.test(
        href
      )
    );
  }

  function isCheckoutButton(
    target
  ) {
    if (!target) {
      return false;
    }

    const name = String(
      target.getAttribute("name") || ""
    ).toLowerCase();

    const id = String(
      target.id || ""
    ).toLowerCase();

    const className =
      typeof target.className === "string"
        ? target.className.toLowerCase()
        : "";

    const type = String(
      target.getAttribute("type") || ""
    ).toLowerCase();

    if (
      name === "checkout" ||
      id === "checkout"
    ) {
      return true;
    }

    if (
      name === "goto_pp" ||
      name === "goto_gc"
    ) {
      return false;
    }

    if (
      type === "submit" &&
      /checkout/.test(className)
    ) {
      return true;
    }

    return /(?:^|[\s_-])checkout(?:[\s_-]|$)/.test(
      className
    );
  }

  function goToPrefillCheckout(
    baseHref
  ) {
    const fallback =
      shopifyRoot + "checkout";

    window.location.href =
      buildCheckoutUrl(
        baseHref || fallback
      );
  }

  /* =========================================================
     REWRITE CHECKOUT LINKS
  ========================================================= */

  function rewriteCheckoutLinks(
    rootEl
  ) {
    const address =
      getStoredGiftCheckoutAddress();

    if (!address) {
      return;
    }

    const scope =
      rootEl &&
      rootEl.querySelectorAll
        ? rootEl
        : document;

    scope
      .querySelectorAll(
        'a[href*="checkout"]'
      )
      .forEach(function (link) {
        if (
          isAcceleratedCheckoutTarget(
            link
          )
        ) {
          return;
        }

        const href =
          link.getAttribute("href");

        if (
          !isRegularCheckoutHref(
            href
          )
        ) {
          return;
        }

        const nextHref =
          buildCheckoutUrl(href);

        if (
          nextHref !== href
        ) {
          link.setAttribute(
            "href",
            nextHref
          );
        }
      });
  }

  /* =========================================================
     ACCELERATED CHECKOUT
  ========================================================= */

  function updateAcceleratedCheckoutVisibility() {
    const hasGiftAddress =
      Boolean(
        getStoredGiftCheckoutAddress()
      );

    const display =
      hasGiftAddress
        ? "none"
        : "";

    document
      .querySelectorAll(
        ".additional-checkout-buttons, " +
        ".dynamic-checkout, " +
        "[data-shopify-buttoncontainer], " +
        ".shopify-payment-button"
      )
      .forEach(function (el) {
        el.style.display =
          display;
      });
  }

  /* =========================================================
     RESTORE ADDRESS FROM CART
  ========================================================= */

  function restoreGiftAddressFromCart() {
    return fetch(
      shopifyRoot + "cart.js",
      {
        method: "GET",
        headers: {
          Accept: "application/json"
        }
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error(
            "Unable to read cart."
          );
        }

        return response.json();
      })
      .then(function (cart) {
        if (
          !cart ||
          !Array.isArray(
            cart.items
          ) ||
          cart.items.length === 0
        ) {
          rewriteCheckoutLinks(
            document
          );

          updateAcceleratedCheckoutVisibility();

          return null;
        }

        const fromAttributes =
          buildCheckoutAddressFromAttributes(
            cart.attributes || {}
          );

        if (fromAttributes) {
          storeGiftCheckoutAddress(
            fromAttributes
          );

          rewriteCheckoutLinks(
            document
          );

          updateAcceleratedCheckoutVisibility();

          return fromAttributes;
        }

        if (
          !cartLooksLikeGift(
            cart
          )
        ) {
          /*
            Do not clear the local address
            aggressively here because user may
            still be moving through the gift flow.
          */
        }

        rewriteCheckoutLinks(
          document
        );

        updateAcceleratedCheckoutVisibility();

        return getStoredGiftCheckoutAddress();
      })
      .catch(function (error) {
        console.warn(
          "🎁 Cart address restore failed:",
          error
        );

        return getStoredGiftCheckoutAddress();
      });
  }

  /* =========================================================
     SAVE CART ATTRIBUTES
  ========================================================= */

  async function saveGiftCartAttributes(
    cartProperties,
    checkoutAddress
  ) {
    let existing = {};

    try {
      const response =
        await fetch(
          shopifyRoot +
            "cart.js",
          {
            method: "GET",
            headers: {
              Accept:
                "application/json"
            }
          }
        );

      if (response.ok) {
        const cart =
          await response.json();

        existing =
          cart.attributes || {};
      }
    } catch (error) {
      existing = {};
    }

    const attributes =
      Object.assign(
        {},
        existing,
        {
          "Gift Order": "Yes",

          "Recipient Name":
            cartProperties[
              "Recipient Name"
            ] || "",

          "Recipient Address":
            cartProperties[
              "Recipient Address"
            ] || "",

          "Recipient Apartment":
            cartProperties[
              "Recipient Apartment"
            ] || "",

          "Recipient City":
            cartProperties[
              "Recipient City"
            ] || "",

          "Recipient State":
            cartProperties[
              "Recipient State"
            ] || "",

          "Recipient Postal Code":
            cartProperties[
              "Recipient Postal Code"
            ] || "",

          "Recipient Country":
            cartProperties[
              "Recipient Country"
            ] || "",

          "Recipient Phone":
            cartProperties[
              "Recipient Phone"
            ] || "",

          "Gift Message":
            cartProperties[
              "Gift Message"
            ] || "",

          _gift_shipping_address:
            JSON.stringify(
              checkoutAddress
            ),

          _gift_country:
            checkoutAddress.country ||
            "",

          _gift_province:
            checkoutAddress.province ||
            ""
        }
      );

    const response =
      await fetch(
        shopifyRoot +
          "cart/update.js",
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
            Accept:
              "application/json"
          },
          body: JSON.stringify({
            attributes:
              attributes
          })
        }
      );

    if (!response.ok) {
      throw new Error(
        "Unable to save gift address to cart."
      );
    }

    return response.json();
  }

  /* =========================================================
     CHECKOUT CLICK INTERCEPT
  ========================================================= */

  document.addEventListener(
    "click",
    function (event) {
      const address =
        getStoredGiftCheckoutAddress();

      if (!address) {
        return;
      }

      const target =
        event.target &&
        event.target.closest
          ? event.target.closest(
              "a[href], button, input[type='submit']"
            )
          : null;

      if (!target) {
        return;
      }

      if (
        isAcceleratedCheckoutTarget(
          target
        )
      ) {
        return;
      }

      const href =
        target.getAttribute(
          "href"
        ) || "";

      if (
        isRegularCheckoutHref(
          href
        )
      ) {
        event.preventDefault();
        event.stopPropagation();

        goToPrefillCheckout(
          href
        );

        return;
      }

      if (
        isCheckoutButton(
          target
        )
      ) {
        const form =
          target.closest("form");

        const action =
          form
            ? form.getAttribute(
                "action"
              ) || ""
            : "";

        if (
          !form ||
          /\/cart/.test(action) ||
          action === ""
        ) {
          event.preventDefault();
          event.stopPropagation();

          goToPrefillCheckout(
            shopifyRoot +
              "checkout"
          );
        }
      }
    },
    true
  );

  /* =========================================================
     CHECKOUT SUBMIT INTERCEPT
  ========================================================= */

  document.addEventListener(
    "submit",
    function (event) {
      const address =
        getStoredGiftCheckoutAddress();

      if (!address) {
        return;
      }

      const form =
        event.target;

      if (
        !form ||
        !form.getAttribute
      ) {
        return;
      }

      const action =
        String(
          form.getAttribute(
            "action"
          ) || ""
        ).toLowerCase();

      if (
        action &&
        action.indexOf(
          "/cart"
        ) === -1
      ) {
        return;
      }

      const submitter =
        event.submitter;

      if (
        submitter &&
        isAcceleratedCheckoutTarget(
          submitter
        )
      ) {
        return;
      }

      const checkoutField =
        form.querySelector(
          '[name="checkout"]'
        );

      const submitIsCheckout =
        submitter &&
        isCheckoutButton(
          submitter
        );

      if (
        !submitIsCheckout &&
        !checkoutField
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      goToPrefillCheckout(
        shopifyRoot +
          "checkout"
      );
    },
    true
  );

  /* =========================================================
     POPUP
  ========================================================= */

  const modal =
    document.querySelector(
      "[data-gift-modal]"
    );

  if (!modal) {
    console.warn(
      "🎁 Gift: popup markup not found."
    );
  }

  if (modal) {
    const form =
      modal.querySelector(
        "[data-gift-form]"
      );

    const errorBox =
      modal.querySelector(
        "[data-gift-error]"
      );

    const successMessage =
      modal.querySelector(
        "[data-gift-success-message]"
      );

    const submitButton =
      modal.querySelector(
        ".gift-add-button"
      );

    const submitText =
      modal.querySelector(
        "[data-gift-submit-text]"
      );

    const loadingText =
      modal.querySelector(
        "[data-gift-loading]"
      );

    /* =======================================================
       MOVE POPUP TO BODY
    ======================================================= */

    if (
      modal.parentElement !==
      document.body
    ) {
      document.body.appendChild(
        modal
      );
    }

    /* =======================================================
       POPUP OPEN
    ======================================================= */

    function openGiftModal() {
      modal.classList.remove(
        "is-open"
      );

      modal.setAttribute(
        "aria-hidden",
        "false"
      );

      document.body.classList.add(
        "gift-modal-open"
      );

      requestAnimationFrame(
        function () {
          requestAnimationFrame(
            function () {
              modal.classList.add(
                "is-open"
              );
            }
          );
        }
      );

      const firstInput =
        modal.querySelector(
          "input:not([type='hidden']), select, textarea"
        );

      if (firstInput) {
        setTimeout(
          function () {
            firstInput.focus();
          },
          100
        );
      }
    }

    /* =======================================================
       POPUP CLOSE
    ======================================================= */

    function closeGiftModal() {
      modal.classList.remove(
        "is-open"
      );

      modal.setAttribute(
        "aria-hidden",
        "true"
      );

      document.body.classList.remove(
        "gift-modal-open"
      );
    }

    /* =======================================================
       FORM HELPERS
    ======================================================= */

    function getFormValue(name) {
      const field =
        form &&
        form.elements
          ? form.elements[name]
          : null;

      return field
        ? String(
            field.value || ""
          ).trim()
        : "";
    }

    function showError(message) {
      if (!errorBox) {
        alert(message);
        return;
      }

      errorBox.textContent =
        message;

      errorBox.hidden = false;
    }

    function hideError() {
      if (!errorBox) {
        return;
      }

      errorBox.textContent = "";

      errorBox.hidden = true;
    }

    function setLoading(
      isLoading
    ) {
      if (submitButton) {
        submitButton.disabled =
          isLoading;
      }

      if (submitText) {
        submitText.hidden =
          isLoading;
      }

      if (loadingText) {
        loadingText.hidden =
          !isLoading;
      }
    }

    /* =======================================================
       POPUP CLICK HANDLERS
    ======================================================= */

    document.addEventListener(
      "click",
      function (event) {
        const openButton =
          event.target &&
          event.target.closest
            ? event.target.closest(
                "[data-gift-open]"
              )
            : null;

        if (openButton) {
          event.preventDefault();
          event.stopPropagation();

          const selectedGiftCard =
            getSelectedGiftCard();

          const selectedProducts =
            getSelectedProducts();

          if (
            !selectedGiftCard
          ) {
            showError(
              "Please select a gift card first."
            );

            return;
          }

          if (
            selectedProducts.length ===
            0
          ) {
            showError(
              "Please select at least one gift product first."
            );

            return;
          }

          hideError();

          closeGiftModal();

          setTimeout(
            function () {
              openGiftModal();
            },
            20
          );

          return;
        }

        const closeButton =
          event.target &&
          event.target.closest
            ? event.target.closest(
                "[data-gift-close]"
              )
            : null;

        if (
          closeButton &&
          modal.contains(
            closeButton
          )
        ) {
          event.preventDefault();
          event.stopPropagation();

          closeGiftModal();
        }
      }
    );

    /* =======================================================
       ESCAPE KEY
    ======================================================= */

    document.addEventListener(
      "keydown",
      function (event) {
        if (
          event.key === "Escape" &&
          modal.classList.contains(
            "is-open"
          )
        ) {
          closeGiftModal();
        }
      }
    );

    /* =======================================================
       FORM
    ======================================================= */

    if (!form) {
      console.warn(
        "🎁 Gift: form markup not found."
      );
    } else {

      /* =====================================================
         REMOVE EXISTING GIFT ITEMS
      ===================================================== */

      async function removeExistingGiftItems(
        giftCard,
        selectedProducts
      ) {
        try {
          const cartResponse =
            await fetch(
              shopifyRoot +
                "cart.js",
              {
                method: "GET",
                headers: {
                  Accept:
                    "application/json"
                }
              }
            );

          if (!cartResponse.ok) {
            throw new Error(
              "Unable to read current cart."
            );
          }

          const cart =
            await cartResponse.json();

          if (
            !cart ||
            !Array.isArray(
              cart.items
            )
          ) {
            return;
          }

          const variantIdsToReplace =
            {};

          getLastGiftVariantIds().forEach(
            function (id) {
              variantIdsToReplace[
                Number(id)
              ] = true;
            }
          );

          if (
            giftCard &&
            giftCard.variantId
          ) {
            variantIdsToReplace[
              Number(
                giftCard.variantId
              )
            ] = true;
          }

          (selectedProducts ||
            []).forEach(
            function (product) {
              if (
                !product ||
                !product.variantId
              ) {
                return;
              }

              variantIdsToReplace[
                Number(
                  product.variantId
                )
              ] = true;
            }
          );

          const isGiftCart =
            (cart.attributes ||
              {})["Gift Order"] ===
              "Yes" ||
            Boolean(
              (cart.attributes ||
                {})
                ._gift_shipping_address
            );

          const updates = {};

          cart.items.forEach(
            function (item) {
              if (!item) {
                return;
              }

              const properties =
                item.properties ||
                {};

              const variantId =
                Number(
                  item.variant_id ||
                    item.id
                );

              const isGiftItem =
                properties[
                  "Gift Order"
                ] === "Yes" ||
                properties[
                  "Gift Item Type"
                ] === "Gift Card" ||
                properties[
                  "Gift Item Type"
                ] ===
                  "Gift Product";

              const isPreviousGiftVariant =
                Boolean(
                  variantId &&
                    variantIdsToReplace[
                      variantId
                    ]
                );

              const propertyKeys =
                Object.keys(
                  properties
                ).filter(
                  function (
                    key
                  ) {
                    return (
                      properties[
                        key
                      ] !==
                        null &&
                      properties[
                        key
                      ] !== ""
                    );
                  }
                );

              const isUntaggedGiftCartItem =
                isGiftCart &&
                propertyKeys.length ===
                  0;

              if (
                !isGiftItem &&
                !isPreviousGiftVariant &&
                !isUntaggedGiftCartItem
              ) {
                return;
              }

              if (item.key) {
                updates[
                  item.key
                ] = 0;
              }

              if (variantId) {
                updates[
                  String(
                    variantId
                  )
                ] = 0;
              }
            }
          );

          if (
            Object.keys(
              updates
            ).length === 0
          ) {
            return;
          }

          const updateResponse =
            await fetch(
              shopifyRoot +
                "cart/update.js",
              {
                method: "POST",
                headers: {
                  "Content-Type":
                    "application/json",
                  Accept:
                    "application/json"
                },
                body:
                  JSON.stringify({
                    updates:
                      updates
                  })
              }
            );

          if (
            !updateResponse.ok
          ) {
            throw new Error(
              "Unable to remove previous gift items."
            );
          }

          console.log(
            "🎁 Previous gift items removed."
          );
        } catch (error) {
          console.error(
            "🎁 Error removing previous gift items:",
            error
          );

          throw error;
        }
      }

      /* =====================================================
         FORM SUBMIT
      ===================================================== */

      form.addEventListener(
        "submit",
        async function (event) {
          event.preventDefault();
          event.stopPropagation();

          hideError();

          const recipientName =
            getFormValue(
              "gift_recipient_name"
            );

          const recipientAddress =
            getFormValue(
              "gift_recipient_address"
            );

          const recipientApartment =
            getFormValue(
              "gift_recipient_apartment"
            );

          const recipientCity =
            getFormValue(
              "gift_recipient_city"
            );

          const recipientcounty =
            getFormValue(
              "gift_recipient_county"
            );

          const recipientPostal =
            getFormValue(
              "gift_recipient_postal"
            );

          const giftMessage =
            getFormValue(
              "gift_message"
            );

          /* =================================================
             VALIDATION
          ================================================= */

          if (
            !recipientName ||
            !recipientAddress ||
            !recipientCity ||
            !recipientPostal 
            
          ) {
            showError(
              "Please fill in all required fields."
            );

            return;
          }

          const giftCard =
            getSelectedGiftCard();

          const selectedProducts =
            getSelectedProducts();

          if (!giftCard) {
            showError(
              "Your gift card selection could not be found. Please go back and select a gift card."
            );

            return;
          }

          if (
            selectedProducts.length ===
            0
          ) {
            showError(
              "Please select at least one gift product."
            );

            return;
          }

          if (successMessage) {
            successMessage.classList.remove(
              "is-visible"
            );
          }

          setLoading(true);

          try {

            /* ===============================================
               CART PROPERTIES
            =============================================== */

            const cartProperties = {
              "Gift Order":
                "Yes",

              "Recipient Name":
                recipientName,

              "Recipient Address":
                recipientAddress,

              "Recipient Apartment":
                recipientApartment,

              "Recipient City":
                recipientCity,

              "Recipient County":
                recipientcounty,

              "Recipient Postal Code":
                recipientPostal,

              "Gift Message":
                giftMessage
            };

            /* ===============================================
               CHECKOUT ADDRESS
            =============================================== */

            const checkoutAddress =
              buildCheckoutAddressFromForm(
                form,
                {
                  recipientName:
                    recipientName,

                  recipientAddress:
                    recipientAddress,

                  recipientApartment:
                    recipientApartment,

                  recipientCity:
                    recipientCity,

                  recipientPostal:
                    recipientPostal,

        
                }
              );

            /* ===============================================
               REMOVE PREVIOUS GIFT
            =============================================== */

            await removeExistingGiftItems(
              giftCard,
              selectedProducts
            );

            /* ===============================================
               ITEMS
            =============================================== */

            const items = [];

            /* ===============================================
               GIFT CARD
               
               IMPORTANT:
               Only recipient information is stored.
               
               NOT stored:
               - Design
               - Gift Item Type
               - Gift Card Design
            =============================================== */

            items.push({
              id: Number(
                giftCard.variantId
              ),

              quantity: 1,

              properties: {
                "Gift Order":
                  "Yes",

                "Recipient Name":
                  recipientName,

                "Recipient Address":
                  recipientAddress,

                "Recipient Apartment":
                  recipientApartment,

                "Recipient City":
                  recipientCity,

                "Recipient County":
                  recipientcounty,

                "Recipient Postal Code":
                  recipientPostal,

                "Gift Message":
                  giftMessage
              }
            });

            /* ===============================================
               GIFT PRODUCTS

               Always add quantity 1.
               Previous matching cart lines are removed first
               so Shopify does not increase old quantities.
            =============================================== */

            const uniqueGiftProducts = [];
            const addedVariantIds = new Set();

            selectedProducts.forEach(function (product) {
              const variantId = Number(product.variantId);

              if (!variantId) {
                return;
              }

              if (addedVariantIds.has(variantId)) {
                return;
              }

              addedVariantIds.add(variantId);

              uniqueGiftProducts.push({
                id: variantId,
                quantity: 1,
                properties: {
                  "Gift Order": "Yes",
                  "Gift Item Type": "Gift Product"
                }
              });
            });

            uniqueGiftProducts.forEach(function (product) {
              items.push(product);
            });

            if (
              items.length === 0
            ) {
              throw new Error(
                "No valid gift products were found."
              );
            }

            /* ===============================================
               ADD TO CART
            =============================================== */

            const response =
              await fetch(
                shopifyRoot +
                  "cart/add.js",
                {
                  method: "POST",

                  headers: {
                    "Content-Type":
                      "application/json",

                    Accept:
                      "application/json"
                  },

                  body:
                    JSON.stringify({
                      items:
                        items
                    })
                }
              );

            let result = null;

            try {
              result =
                await response.json();
            } catch (
              jsonError
            ) {
              result = null;
            }

            if (!response.ok) {
              throw new Error(
                (result &&
                  (
                    result.description ||
                    result.message
                  )) ||
                "Unable to add your gift to the cart."
              );
            }

            console.log(
              "🎁 Gift items added:",
              result
            );

            saveLastGiftVariantIds(
              items.map(function (item) {
                return item.id;
              })
            );

            /* ===============================================
               SAVE CHECKOUT ADDRESS
            =============================================== */

            storeGiftCheckoutAddress(
              checkoutAddress
            );

            /* ===============================================
               SAVE CART ATTRIBUTES
            =============================================== */

            try {
              await saveGiftCartAttributes(
                cartProperties,
                checkoutAddress
              );

              console.log(
                "🎁 Gift checkout address saved."
              );
            } catch (
              attributeError
            ) {
              console.warn(
                "🎁 Cart attribute save failed, but local checkout address is saved:",
                attributeError
              );
            }

            /* ===============================================
               SUCCESS MESSAGE
            =============================================== */

            setLoading(false);

            if (
              successMessage
            ) {
              successMessage.classList.add(
                "is-visible"
              );
            }

            localStorage.removeItem(
              "selectedGiftCardVariant"
            );

            setTimeout(
              function () {

                closeGiftModal();

                window.location.href =
                  shopifyRoot +
                  "cart";

              },
              2000
            );

          } catch (error) {

            console.error(
              "🎁 Gift error:",
              error
            );

            setLoading(false);

            showError(
              error.message ||
                "Something went wrong. Please try again."
            );
          }
        }
      );
    }

    /* =======================================================
       INITIAL POPUP STATE
    ======================================================= */

    modal.classList.remove(
      "is-open"
    );

    modal.setAttribute(
      "aria-hidden",
      "true"
    );

    document.body.classList.remove(
      "gift-modal-open"
    );
  }

  /* =========================================================
     INITIAL CHECKOUT STATE
  ========================================================= */

  rewriteCheckoutLinks(
    document
  );

  updateAcceleratedCheckoutVisibility();

  restoreGiftAddressFromCart();

  /* =========================================================
     MUTATION OBSERVER
  ========================================================= */

  if (
    window.MutationObserver
  ) {
    const observer =
      new MutationObserver(
        function (
          mutations
        ) {
          mutations.forEach(
            function (mutation) {
              mutation.addedNodes.forEach(
                function (node) {
                  if (
                    !node ||
                    node.nodeType !== 1
                  ) {
                    return;
                  }

                  rewriteCheckoutLinks(
                    node
                  );

                  updateAcceleratedCheckoutVisibility();
                }
              );
            }
          );
        }
      );

    if (document.body) {
      observer.observe(
        document.body,
        {
          childList: true,
          subtree: true
        }
      );
    }
  }

  

  /* =========================================================
     PAGE SHOW / BROWSER BACK FIX
  ========================================================= */

  window.addEventListener(
    "pageshow",
    function () {
      const popup =
        document.querySelector(
          "[data-gift-modal]"
        );

      if (!popup) {
        return;
      }

      popup.classList.remove(
        "is-open"
      );

      popup.setAttribute(
        "aria-hidden",
        "true"
      );

      document.body.classList.remove(
        "gift-modal-open"
      );

      rewriteCheckoutLinks(
        document
      );

      updateAcceleratedCheckoutVisibility();

      console.log(
        "🎁 Gift popup reset after page navigation."
      );
    }
  );

  console.log(
    "🎁 Gift popup + checkout functionality initialized."
  );





/* =========================================================
   GIFT CARD - HIDE QUANTITY BOX ONLY
   REMOVE BUTTON MUST STAY VISIBLE
   ========================================================= */

(function () {
  "use strict";

  async function hideGiftCardQuantityBox() {
    try {
      const root =
        (window.Shopify &&
          window.Shopify.routes &&
          window.Shopify.routes.root) ||
        "/";

      const response = await fetch(
        root + "cart.js",
        {
          method: "GET",
          headers: {
            Accept: "application/json"
          },
          cache: "no-store"
        }
      );

      if (!response.ok) {
        return;
      }

      const cart = await response.json();

      if (
        !cart ||
        !Array.isArray(cart.items)
      ) {
        return;
      }

      /*
       * Shopify cart items and .cart-item
       * elements follow the same order.
       */
      const cartRows =
        document.querySelectorAll(
          ".cart-item"
        );

      cart.items.forEach(function (item, index) {

        if (!item) {
          return;
        }

        const properties =
          item.properties || {};

        /*
         * THIS IS THE GIFT CARD:
         *
         * Gift Order = Yes
         * Gift Item Type = NOT Gift Product
         */
        const isGiftCard =
          String(
            properties["Gift Order"] || ""
          ).trim().toLowerCase() === "yes" &&
          String(
            properties["Gift Item Type"] || ""
          ).trim().toLowerCase() !==
            "gift product";

        if (!isGiftCard) {
          return;
        }

        const cartRow =
          cartRows[index];

        if (!cartRow) {
          return;
        }

        /*
         * Hide ONLY the quantity box.
         *
         * Do NOT hide the parent quantity wrapper,
         * because REMOVE button is inside that area.
         */
        cartRow
          .querySelectorAll(
            "quantity-input.quantity, " +
            "quantity-input, " +
            ".quantity"
          )
          .forEach(function (quantityBox) {

            quantityBox.style.display =
              "none";
          });

        console.log(
          "🎁 Gift Card quantity button hidden:",
          item.title
        );
      });

    } catch (error) {
      console.warn(
        "🎁 Gift Card quantity hide error:",
        error
      );
    }
  }

  /*
   * Initial load
   */
  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      function () {
        setTimeout(
          hideGiftCardQuantityBox,
          300
        );
      }
    );
  } else {
    setTimeout(
      hideGiftCardQuantityBox,
      300
    );
  }

  /*
   * Shopify cart re-render protection
   */
  if (window.MutationObserver) {

    let timer = null;

    const observer =
      new MutationObserver(function () {

        clearTimeout(timer);

        timer = setTimeout(
          function () {
            hideGiftCardQuantityBox();
          },
          250
        );
      });

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }

})();












  
});